// src/pages/ThemeEditorDynamic.jsx
import React, { useMemo, useState, useEffect, useCallback, useContext } from "react";
import { Row, Col, Card, message } from "antd";
import AppContext from "../contexts/AppContext";
import FloatingFooter from "../components/utils/FloatingFooter";

import PhonePreview from "../components/themeEditor/PhonePreview";
import ColorLibrary from "../components/themeEditor/ColorLibrary";
import ConfigOptions from "../components/themeEditor/ConfigOptions";
import DeviceControls from "../components/themeEditor/DeviceControls";

// 工具函数与常量（确保这些方法在 utils 中存在）
import {
  normalizeHex,
  ensureInPalette,
  validateAll,
  DUAL_MAPPING,
  dualLiteralToHex,
  CONFIG_KEYS,
  ensureExactConfigKeys,
} from "../components/themeEditor/utils";

export default function ThemeEditorDynamic() {
  /** ========= 上下文与环境 ========= */
  const { token, selectedRole } = useContext(AppContext);
  const venueId = selectedRole?.id;
  const apiBase = process.env.REACT_APP_API_BASE_URL;

  /** ========= 左侧颜色库（删除模式） ========= */
  const [deleteMode, setDeleteMode] = useState(false);
  const [deleteSet, setDeleteSet] = useState(new Set());
  const togglePickToDelete = (i) =>
    setDeleteSet((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  /** ========= Loading ========= */
  const [loading, setLoading] = useState(false);

  //========= 是否已“知道了”提示 ========
  const [gotIt, setGotIt] = useState(() => {
    try {
      return localStorage.getItem("theme_preview_got_it") === "1";
    } catch {
      return false;
    }
  });

  /** ========= 预览设备与缩放 ========= */
  const PRESET_GROUPS = useMemo(
    () => ({
      mobile: [
        { id: "iphone15pro", name: "iPhone 15 Pro", w: 393, h: 852 },
        { id: "iphone14promax", name: "iPhone 14 Pro Max", w: 430, h: 932 },
        { id: "pixel8", name: "Pixel 8", w: 412, h: 915 },
        { id: "galaxyS24", name: "Galaxy S24", w: 412, h: 915 },
        { id: "ipadMini", name: "iPad mini (Portrait)", w: 768, h: 1024 },
        { id: "mobile-custom", name: "Custom", w: 390, h: 844, isCustom: true },
      ],
      touchscreen: [
        { id: "touch-portrait-1080", name: "Touchscreen · 1080×1920", w: 1080, h: 1920 },
        { id: "touch-portrait-768", name: "Touchscreen · 768×1366", w: 768, h: 1366 },
        { id: "touch-landscape-1920", name: "Touchscreen · 1920×1080", w: 1920, h: 1080 },
        { id: "touch-custom", name: "Custom", w: 1080, h: 1920, isCustom: true },
      ],
    }),
    []
  );

  const previewTargetOptions = useMemo(
    () => [
      { value: "mobile", label: "Mobile app" },
      { value: "touchscreen", label: "Touchscreen" },
    ],
    []
  );

  const [previewTarget, setPreviewTarget] = useState("mobile");

  const PRESETS = useMemo(
    () => PRESET_GROUPS[previewTarget] || [],
    [PRESET_GROUPS, previewTarget]
  );

  const [deviceIdByTarget, setDeviceIdByTarget] = useState(() => ({
    mobile: PRESET_GROUPS.mobile?.[0]?.id,
    touchscreen: PRESET_GROUPS.touchscreen?.[0]?.id,
  }));

  const deviceId = deviceIdByTarget[previewTarget] || PRESETS[0]?.id;

  useEffect(() => {
    if (!deviceId && PRESETS[0]?.id) {
      setDeviceIdByTarget((prev) => ({ ...prev, [previewTarget]: PRESETS[0].id }));
    }
  }, [deviceId, PRESETS, previewTarget]);

  const setDeviceId = useCallback(
    (nextId) => {
      setDeviceIdByTarget((prev) => ({ ...prev, [previewTarget]: nextId }));
    },
    [previewTarget]
  );

  const activePreset = useMemo(
    () => PRESETS.find((d) => d.id === deviceId) || PRESETS[0],
    [deviceId, PRESETS]
  );

  const [sizeByTarget, setSizeByTarget] = useState(() => ({
    mobile: PRESET_GROUPS.mobile?.[0]
      ? { w: PRESET_GROUPS.mobile[0].w, h: PRESET_GROUPS.mobile[0].h }
      : { w: 360, h: 780 },
    touchscreen: PRESET_GROUPS.touchscreen?.[0]
      ? { w: PRESET_GROUPS.touchscreen[0].w, h: PRESET_GROUPS.touchscreen[0].h }
      : { w: 1080, h: 1920 },
  }));

  const size = sizeByTarget[previewTarget]
    || (activePreset ? { w: activePreset.w, h: activePreset.h } : { w: 360, h: 780 });

  const setSize = useCallback(
    (next) => {
      setSizeByTarget((prev) => {
        const current = prev[previewTarget]
          || (activePreset ? { w: activePreset.w, h: activePreset.h } : { w: 360, h: 780 });
        const resolved = typeof next === "function" ? next(current) : next;
        return { ...prev, [previewTarget]: resolved };
      });
    },
    [previewTarget, activePreset]
  );

  useEffect(() => {
    if (!activePreset || activePreset.isCustom) return;
    setSizeByTarget((prev) => {
      const current = prev[previewTarget];
      if (current && current.w === activePreset.w && current.h === activePreset.h) return prev;
      return { ...prev, [previewTarget]: { w: activePreset.w, h: activePreset.h } };
    });
  }, [activePreset, previewTarget]);

  const [scaleByTarget, setScaleByTarget] = useState(() => ({
    mobile: gotIt ? 0.68 : 0.6,
    touchscreen: 0.4,
  }));

  const scale =
    scaleByTarget[previewTarget] ?? (previewTarget === "mobile" ? (gotIt ? 0.68 : 0.6) : 0.4);

  const setScale = useCallback(
    (next) => {
      setScaleByTarget((prev) => {
        const current = prev[previewTarget]
          ?? (previewTarget === "mobile" ? (gotIt ? 0.68 : 0.6) : 0.4);
        const resolved = typeof next === "function" ? next(current) : next;
        return { ...prev, [previewTarget]: resolved };
      });
    },
    [previewTarget, gotIt]
  );

  const touchscreenPreviewOrigin = useMemo(() => {
    if (process.env.REACT_APP_TOUCHSCREEN_PREVIEW_ORIGIN) {
      return process.env.REACT_APP_TOUCHSCREEN_PREVIEW_ORIGIN;
    }
    if (process.env.NODE_ENV === "development") {
      return "http://localhost:3000";
    }
    if (venueId) {
      return `https://${venueId}.touchandexplore.com`;
    }
    return "https://touchscreen.platypus360.com";
  }, [venueId]);

  const buildTouchscreenSrc = useCallback(
    ({ origin: baseOrigin, venueId: targetId }) => {
      try {
        const resolved = new URL(baseOrigin || touchscreenPreviewOrigin);
        const params = new URLSearchParams();
        if (targetId) params.set("venue_id", targetId);
        params.set("preview", "1");
        // No path segment: touchandexplore.com is a plain S3/CloudFront static site
        // with no SPA-fallback configured, so anything beyond "/" 404s at the CDN.
        // App.js's BrowserRouter catch-all renders MainPage regardless of pathname
        // anyway, so root + query string is the only URL guaranteed to load.
        return `${resolved.origin}/?${params.toString()}`;
      } catch {
        return "";
      }
    },
    [touchscreenPreviewOrigin]
  );

  /** ========= 主题数据状态 ========= */
  const MAX_COLORS = 10;
  const [palette, setPalette] = useState([]); // theme.colors
  const [configKeys, setConfigKeys] = useState([]); // Object.keys(configuration)，此处用 CONFIG_KEYS 统一
  const [cfgIndex, setCfgIndex] = useState({}); // key -> palette index
  const [originalConf, setOriginalConf] = useState({}); // 后端原始 configuration
  const [hasSpecialColor, setHasSpecialColor] = useState(false); // DB 是否存在 legacy `color`
  const [initialEditorState, setInitialEditorState] = useState(null);

  // 模式：Dual（opt2）、Multiple（opt3）
  const [selectionMode, setSelectionMode] = useState("opt2");

  /** ========= Dual 模式联动：primary/secondary 改动 -> 其他字段按映射填充 ========= */
  useEffect(() => {
    if (selectionMode !== "opt2") return;

    const pIdx = cfgIndex.primaryColor;
    const sIdx = cfgIndex.secondaryColor;
    const primaryHex =
      typeof pIdx === "number" && palette[pIdx]
        ? normalizeHex(palette[pIdx])
        : normalizeHex(originalConf?.primaryColor || "");
    const secondaryHex =
      typeof sIdx === "number" && palette[sIdx]
        ? normalizeHex(palette[sIdx])
        : normalizeHex(originalConf?.secondaryColor || "");

    if (!primaryHex && !secondaryHex) return;

    // Dual 下要求调色盘至少包含：主/副 + 黑白
    const REQUIRED = [primaryHex, secondaryHex, "#000000", "#ffffff"]
      .map(normalizeHex)
      .filter(Boolean);

    let nextColors = palette;
    const hexToIndex = {};
    REQUIRED.forEach((hex) => {
      const r = ensureInPalette(nextColors, hex);
      nextColors = r.colors;
      hexToIndex[hex] = r.index;
    });

    if (nextColors.length !== palette.length) {
      setPalette(nextColors);
    }

    const isEmpty = (v) => v == null || (typeof v === "string" && v.trim() === "");

    // 回填 cfgIndex：仅当该字段既没有 DB 固定值、也没被手动选择时才按映射补
    setCfgIndex((prev) => {
      const next = { ...prev };

      if (primaryHex) next.primaryColor = hexToIndex[primaryHex];
      if (secondaryHex) next.secondaryColor = hexToIndex[secondaryHex];

      Object.entries(DUAL_MAPPING).forEach(([key, token]) => {
        const originalHex = normalizeHex(originalConf?.[key] || "");
        const hasOriginal = !isEmpty(originalHex);
        const hasIndexAlready = typeof prev[key] === "number";
        if (hasOriginal || hasIndexAlready) return;

        if (token === "primaryColor" && primaryHex) {
          next[key] = hexToIndex[primaryHex];
        } else if (token === "secondaryColor" && secondaryHex) {
          next[key] = hexToIndex[secondaryHex];
        } else {
          const fixed = dualLiteralToHex(token); // e.g. "#000000" / "#ffffff"
          if (fixed) {
            const idx = nextColors.indexOf(fixed);
            if (idx >= 0) next[key] = idx;
          }
        }
      });

      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionMode, cfgIndex.primaryColor, cfgIndex.secondaryColor, palette, originalConf]);

  /** ========= 切换模式时退出删除模式 ========= */
  useEffect(() => {
    if (deleteMode) {
      message.warning("You are in edit mode. Switching mode will exit edit mode.");
      setDeleteMode(false);
      setDeleteSet(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionMode]);


  /** ========= 通用带鉴权请求 ========= */
  async function authedJSON(url, method, body) {
    const res = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    const txt = await res.text();
    const data = txt ? JSON.parse(txt) : null;
    if (!res.ok) throw new Error((data && data.error) || `HTTP ${res.status}`);
    return data;
  }

  /** ========= 应用后端 theme 到本地状态 ========= */
  const applyThemeFromDB = useCallback((theme) => {
    let colors = Array.isArray(theme?.colors)
      ? theme.colors.map(normalizeHex).filter(Boolean).slice(0, MAX_COLORS)
      : [];
    const conf =
      theme?.configuration && typeof theme.configuration === "object" ? theme.configuration : {};

    setOriginalConf(conf);
    setConfigKeys(CONFIG_KEYS); // 固定使用我们维护的 CONFIG_KEYS 顺序

    // 先按 DB 配置回填 cfgIndex（不含 primary/secondary/color）
    const nextIdx = {};
    Object.keys(conf).forEach((k) => {
      if (["color", "primaryColor", "secondaryColor"].includes(k)) return;
      const hex = normalizeHex(conf[k]);
      if (!hex) {
        nextIdx[k] = undefined;
        return;
      }
      const found = colors.indexOf(hex);
      nextIdx[k] = found >= 0 ? found : undefined;
    });

    // legacy color 优先：若 DB 有 color，则 primary = color，secondary = #fff
    const colorHex = normalizeHex(conf?.color);
    const hasColor = !!colorHex;
    if (hasColor) {
      const r1 = ensureInPalette(colors, colorHex);
      colors = r1.colors;
      nextIdx["primaryColor"] = r1.index;

      const r2 = ensureInPalette(colors, "#ffffff");
      colors = r2.colors;
      nextIdx["secondaryColor"] = r2.index;
    } else {
      // 正常 primary/secondary
      const pDb = normalizeHex(conf["primaryColor"]);
      const sDb = normalizeHex(conf["secondaryColor"]);
      if (pDb) {
        const rP = ensureInPalette(colors, pDb);
        colors = rP.colors;
        nextIdx["primaryColor"] = rP.index;
      } else nextIdx["primaryColor"] = undefined;

      if (sDb) {
        const rS = ensureInPalette(colors, sDb);
        colors = rS.colors;
        nextIdx["secondaryColor"] = rS.index;
      } else nextIdx["secondaryColor"] = undefined;
    }

    if (colors.length > MAX_COLORS) colors = colors.slice(0, MAX_COLORS);
    setPalette(colors);
    setHasSpecialColor(hasColor);

    // 有主色（含 legacy color）-> 默认 Dual；否则 Multiple
    const hasPrimary = !!normalizeHex(conf?.primaryColor) || !!normalizeHex(conf?.color);
    setSelectionMode(hasPrimary ? "opt2" : "opt3");
    setCfgIndex(nextIdx);
    setInitialEditorState({
      palette: colors,
      cfgIndex: nextIdx,
      originalConf: conf,
      hasSpecialColor: hasColor,
      selectionMode: hasPrimary ? "opt2" : "opt3",
    });
  }, []);

  /** ========= 加载主题（进来时执行一次） ========= */
  const loadTheme = useCallback(async () => {
    if (!apiBase) return message.error("Missing API base URL");
    if (!venueId) return message.warning("No venue selected.");
    setLoading(true);
    try {
      const data = await authedJSON(`${apiBase}/private/venue/${venueId}/theme`, "GET");
      const theme = data?.theme ? data.theme : data;
      applyThemeFromDB(theme);
      message.success("Theme loaded.");
    } catch (e) {
      console.error(e);
      message.error(e.message || "Load failed.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueId, apiBase, applyThemeFromDB, token]);

  useEffect(() => {
    loadTheme();
  }, [loadTheme]);

  /** ========= 删除颜色时同步修改 cfgIndex ========= */
  const removePaletteColor = (i) => {
    setPalette((prev) => {
      if (prev.length <= 1) return prev;

      const removingHex = normalizeHex(prev[i]);

      // Dual 模式下保护：主题色不允许被删除
      if (selectionMode === "opt2") {
        const pIdx = cfgIndex.primaryColor;
        const primaryHex =
          typeof pIdx === "number" && prev[pIdx]
            ? normalizeHex(prev[pIdx])
            : normalizeHex(originalConf?.primaryColor || originalConf?.color || "");
        const isTheme = primaryHex && removingHex === primaryHex;
        const isWhite = removingHex === "#ffffff";
        const isBlack = removingHex === "#000000";
        if (isTheme || isWhite || isBlack) {
          message.warning("In Dual mode, you can’t delete the primary colour, white or black.");
          return prev;
        }
      }

      // 先删除得到新数组
      let next = prev.filter((_, idx) => idx !== i);

      // 重建 cfgIndex 引用（所有 >i 的索引左移 1）
      setCfgIndex((old) => {
        const nu = { ...old };

        // Dual：命中被删索引时，回退到“主题色”索引；Multiple 则回退到 next[0]
        let themeIdxNext = undefined;
        if (selectionMode === "opt2") {
          const pIdx = old.primaryColor;
          themeIdxNext = typeof pIdx === "number" && pIdx !== i ? (pIdx > i ? pIdx - 1 : pIdx) : 0;
        }
        const firstIdxNext = 0;

        Object.keys(nu).forEach((k) => {
          const idx = nu[k];
          if (typeof idx !== "number") return;

          if (idx === i) {
            nu[k] = selectionMode === "opt2" ? themeIdxNext : next.length > 0 ? firstIdxNext : undefined;
          } else if (idx > i) {
            nu[k] = idx - 1;
          }
        });

        return nu;
      });

      return next;
    });
  };

  /** ========= 安全切换模式 ========= */
  const safeSetSelectionMode = (nextMode) => {
    // Dual -> Multiple 时仅提示一次（不立刻清空，保存时清空）
    if (selectionMode === "opt2" && nextMode === "opt3") {
      message.info(
        "Switching to Multiple: Primary/Secondary won't be cleared now; they will be saved as empty when you click Save."
      );
    }
    // 若在删除模式，切换前自动退出
    if (deleteMode) {
      message.warning("Palette edit mode is active. It will be closed automatically.");
      setDeleteMode(false);
      setDeleteSet(new Set());
    }
    setSelectionMode(nextMode);
  };

  /** ========= 构建（预览/保存）载荷：含规范化 & 映射 =========
   * 该载荷既用于保存（落袋），也用于右侧实时预览（不落袋）。
   */
  const buildPayload = useCallback(() => {
    const uniq = (arr) =>
      Array.from(new Set((arr || []).map(normalizeHex).filter(Boolean)));

    let colors = uniq(palette);
    const configuration = {};

    // 读主/副
    const pIdx = cfgIndex.primaryColor;
    const sIdx = cfgIndex.secondaryColor;
    const primaryHex =
      typeof pIdx === "number" && palette[pIdx]
        ? normalizeHex(palette[pIdx])
        : normalizeHex(originalConf?.primaryColor || originalConf?.color || "");
    const secondaryHex =
      typeof sIdx === "number" && palette[sIdx]
        ? normalizeHex(palette[sIdx])
        : normalizeHex(originalConf?.secondaryColor || "");

    // opt2/opt3 分支
    if (selectionMode === "opt2") {
      configuration.color = "";
      configuration.primaryColor = primaryHex;
      configuration.secondaryColor = secondaryHex;
      Object.entries(DUAL_MAPPING).forEach(([key, token]) => {
        let hex = "";
        if (token === "primaryColor") hex = primaryHex;
        else if (token === "secondaryColor") hex = secondaryHex;
        else hex = dualLiteralToHex(token);
        configuration[key] = normalizeHex(hex);
      });
    } else {
      configuration.color = "";
      configuration.primaryColor = "";
      configuration.secondaryColor = "";
      (CONFIG_KEYS || []).forEach((key) => {
        if (key === "color" || key === "primaryColor" || key === "secondaryColor") return;
        const idx = cfgIndex[key];
        let hex = "";
        if (typeof idx === "number" && idx >= 0 && idx < palette.length) {
          hex = normalizeHex(palette[idx]);
        } else {
          hex = normalizeHex(originalConf?.[key] || "");
        }
        if (!hex && colors.length) hex = colors[0];
        configuration[key] = normalizeHex(hex);
      });
    }

    // 并入配置用色
    const configHexes = uniq(Object.values(configuration));
    colors = uniq([...colors, ...configHexes]);

    // Dual 特殊规则
    if (selectionMode === "opt2") {
      const hasLegacyColor = !!normalizeHex(originalConf?.color || "");
      if (hasLegacyColor) {
        colors = uniq([primaryHex, "#000000", "#ffffff", "#374151", "#e5e7eb"]);
      } else {
        const hasPrimaryInColors = !!primaryHex && colors.includes(primaryHex);
        const hasSecondaryInColors = !!secondaryHex && colors.includes(secondaryHex);
        if (!hasPrimaryInColors && primaryHex) colors.push(primaryHex);
        if (!hasSecondaryInColors && secondaryHex) colors.push(secondaryHex);
        colors = uniq(colors);
      }
    }

    // 最多保留 10 个
    const priority = uniq([primaryHex, secondaryHex, "#000000", "#ffffff", "#374151", "#e5e7eb"]);
    let capped = [];
    priority.forEach((hex) => {
      if (hex && colors.includes(hex) && capped.length < MAX_COLORS) capped.push(hex);
    });
    uniq(palette).forEach((hex) => {
      if (capped.length < MAX_COLORS && colors.includes(hex) && !capped.includes(hex)) {
        capped.push(hex);
      }
    });
    colors.forEach((hex) => {
      if (capped.length < MAX_COLORS && !capped.includes(hex)) capped.push(hex);
    });
    if (capped.length === 0) capped = [primaryHex || "#111111"];
    if (colors.length > MAX_COLORS && capped.length === MAX_COLORS) {
      message.warning("The palette exceeded 10 colors. Extra colors were dropped on save.");
    }
    colors = capped;

    // 字段严格对齐
    const strictConf = ensureExactConfigKeys(configuration);

    return { colors, configuration: strictConf };
  }, [palette, cfgIndex, selectionMode, originalConf]);

  /** ========= “实时不落袋”的主题载荷 =========
   * 注意：不再用 key 触发重挂载 iframe，而是把 liveTheme 传给 PhonePreview。
   * PhonePreview 内部通过 postMessage 把该载荷注入到 iframe，右侧立即生效。
   */
  const liveThemePayload = useMemo(() => buildPayload(), [buildPayload]);
  const hasChanges = useMemo(
    () =>
      !!initialEditorState &&
      JSON.stringify({
        palette,
        cfgIndex,
        originalConf,
        hasSpecialColor,
        selectionMode,
      }) !== JSON.stringify(initialEditorState),
    [palette, cfgIndex, originalConf, hasSpecialColor, selectionMode, initialEditorState]
  );

  /** ========= 保存（含字段校验与落袋） ========= */
  const saveTheme = async () => {
    setDeleteMode(false);
    if (!apiBase) return message.error("Missing API base URL.");
    if (!venueId) return message.error("No venue selected.");

    // 业务校验（你已有的验证器）
    const validationErrors = validateAll({
      palette,
      MAX_COLORS,
      cfgIndex,
      selectionMode,
      hasSpecialColor,
      originalConf,
    });
    if (validationErrors.length) {
      validationErrors.forEach((msg) => message.error(msg));
      console.warn("[ThemeEditor] Validation failed:", validationErrors);
      return;
    }

    setLoading(true);
    try {
      const payload = buildPayload();

      if (!payload.colors.length) {
        message.error("Colors cannot be empty.");
        return;
      }

      const data = await authedJSON(
        `${apiBase}/private/venue/${venueId}/theme`,
        "POST",
        payload
      );
      const theme = data?.theme ? data.theme : data;

      applyThemeFromDB(theme); // 落袋成功后以 DB 为准
      message.success("Theme saved.");
    } catch (e) {
      console.error(e);
      message.error(e.message || "Save failed.");
    } finally {
      setLoading(false);
    }
  };

  /** ========= 渲染 ========= */
  return (
    <div className="min-h-screen bg-gray-100 pb-[8rem] overflow-hidden">
      <div className="max-w-7xl w-[92%] mx-auto pt-8 overflow-hidden">
        <div className="-mt-3" style={{ padding: 16 }}>
          <Row gutter={[18, 18]}>
            {/* 左侧：调色库 + 配置 */}
            <Col xs={24} md={12}>
              <ColorLibrary
                palette={palette}
                setPalette={setPalette}
                loading={loading}
                loadTheme={loadTheme}
                MAX_COLORS={MAX_COLORS}
                deleteMode={deleteMode}
                setDeleteMode={setDeleteMode}
                deleteSet={deleteSet}
                togglePickToDelete={togglePickToDelete}
                removePaletteColor={removePaletteColor}
              />

              <ConfigOptions
                selectionMode={selectionMode}
                setSelectionMode={safeSetSelectionMode}
                hasSpecialColor={hasSpecialColor}
                configKeys={configKeys}
                cfgIndex={cfgIndex}
                setCfgIndex={setCfgIndex}
                palette={palette}
              />
            </Col>

            {/* 右侧：设备与预览 */}
            <Col xs={24} md={12}>
              <DeviceControls
                PRESETS={PRESETS}
                deviceId={deviceId}
                setDeviceId={setDeviceId}
                size={size}
                setSize={setSize}
                scale={scale}
                setScale={setScale}
                activePreset={activePreset}
                previewTargets={previewTargetOptions}
                previewTarget={previewTarget}
                onChangePreviewTarget={setPreviewTarget}
              />

              {/* 顶部信息条：仅在未“got it”时显示 */}
              {!gotIt && (
                <div
                  style={{
                    background: "linear-gradient(135deg, rgba(74,92,255,0.12), rgba(74,92,255,0.05))",
                    border: "1px solid rgba(74,92,255,0.18)",
                    color: "#2557d6",
                    padding: "14px 16px",
                    borderRadius: 16,
                    marginBottom: 14,
                    fontSize: 14,
                    lineHeight: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 14,
                    boxShadow: "0 14px 28px rgba(74,92,255,0.08)",
                  }}
                >
                  <span>
                    Live preview only — changes here update the preview immediately, but
                    <strong> won’t affect the real site until you click “Save”.</strong>
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      try { localStorage.setItem("theme_preview_got_it", "1"); } catch { }
                      setGotIt(true);
                    }}
                    style={{
                      background: "#1677ff",
                      color: "#fff",
                      border: 0,
                      borderRadius: 12,
                      padding: "8px 12px",
                      cursor: "pointer",
                      boxShadow: "0 10px 20px rgba(22,119,255,0.24)"
                    }}
                  >
                    Ok
                  </button>
                </div>
              )}

              <Card
                bordered={false}
                className="overflow-hidden rounded-[26px] border border-[#e3e9f8] shadow-[0_18px_40px_rgba(30,52,120,0.08)]"
                bodyStyle={{ padding: 16 }}
              >
                <div style={{ overflow: "auto", padding: 10, background: "linear-gradient(180deg,#fbfcff 0%,#f7f9ff 100%)", borderRadius: 18 }}>
                  {venueId ? (
                    <PhonePreview
                      venueId={venueId}
                      size={size}
                      scale={scale}
                      liveTheme={liveThemePayload}
                      /** 未 got it 时才显示角标 */
                      showRibbon={!gotIt}
                      forcedOrigin={previewTarget === "touchscreen" ? touchscreenPreviewOrigin : undefined}
                      buildPreviewSrc={previewTarget === "touchscreen" ? buildTouchscreenSrc : undefined}
                    />
                  ) : null}
                </div>
              </Card>
            </Col>

          </Row>
        </div>
      </div>

      {/* 底部保存按钮 */}
      <FloatingFooter
        hasChanges={hasChanges}
        onSave={saveTheme}
        saveLabel="Save"
        multipleSave={false}
      />
    </div>
  );
}

